/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Button, Text } from '@/shared/ui';

export interface DraftRecoveryNoticeProps {
  message: string;
  restoreLabel: string;
  discardLabel: string;
  onRestore: () => void;
  onDiscard: () => void;
}

/**
 * Discreet "we recovered a draft" bar with Restore / Discard. The draft is
 * never applied until the user picks Restore.
 *
 * @param props - Resolved texts and the two handlers.
 * @returns The notice element.
 */
export function DraftRecoveryNotice({ message, restoreLabel, discardLabel, onRestore, onDiscard }: DraftRecoveryNoticeProps) {
  return (
    <div
      role="status"
      data-testid="draft-recovery-notice"
      className="mb-(--space-4) flex flex-wrap items-center justify-between gap-(--space-3) rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-subtle) px-(--space-4) py-(--space-3)"
    >
      <Text className="text-[length:var(--text-body-sm)] text-(--text-primary)">{message}</Text>
      <div className="flex gap-(--space-2)">
        <Button variant="outline" onClick={onDiscard}>{discardLabel}</Button>
        <Button variant="solid" onClick={onRestore}>{restoreLabel}</Button>
      </div>
    </div>
  );
}

/**
 * Formats a draft timestamp: time only for today, date and time otherwise.
 *
 * @param savedAt - Epoch milliseconds.
 * @param locale - The i18n language.
 * @returns The label.
 */
export function formatDraftTime(savedAt: number, locale: string): string {
  const date = new Date(savedAt);
  const today = new Date().toDateString() === date.toDateString();
  const options: Intl.DateTimeFormatOptions = today
    ? { hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };
  return date.toLocaleString(locale, options);
}
